import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, X, Eye, Edit2, Check, AlertTriangle, Trash2, FileDown, RefreshCw, RotateCcw, Save, FileText } from 'lucide-react';
import { ConfirmDialog, LoadingButton, validateRequired, validatePercentage } from '../components/QuickWins';
import { EditCycleModal } from '../components/EditCycleModal';
import { EditPayoutModal } from '../components/EditPayoutModal';
import { ClosingWizard } from '../components/ClosingWizard';
import { EditedBadge } from '../components/EditedBadge';
import { PayPayoutModal } from '../components/PayPayoutModal';

const Cycles = () => {
  const [cycles, setCycles] = useState([]);
  const [activeCycle, setActiveCycle] = useState(null);
  const [cycleClients, setCycleClients] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPayoutsModal, setShowPayoutsModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showClearDataModal, setShowClearDataModal] = useState(false);
  const [clearDataStep, setClearDataStep] = useState(1);
  const [clearDataConfirmText, setClearDataConfirmText] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [draftData, setDraftData] = useState(null);
  const [selectedCycleForPayouts, setSelectedCycleForPayouts] = useState(null);
  const [editingDefaults, setEditingDefaults] = useState(false);
  
  // Confirmation dialog state
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [cycleToReopen, setCycleToReopen] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  // Admin edit modals
  const [editCycle, setEditCycle] = useState(null);
  const [editPayout, setEditPayout] = useState(null);

  // Guided closing wizard
  const [showWizard, setShowWizard] = useState(false);
  const [cashPoolINR, setCashPoolINR] = useState(0);
  // Pay payout modal
  const [payoutToPay, setPayoutToPay] = useState(null);
  
  // Company profit input on main page
  const [companyProfit, setCompanyProfit] = useState('');
  
  // Draft distribution state
  const [conversionRate, setConversionRate] = useState(''); // User must enter - no default
  const [clientPayoutModes, setClientPayoutModes] = useState({}); // client_id -> 'INR' | 'USDT'
  
  // Cycle Notes state
  const [cycleNotes, setCycleNotes] = useState([]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [showNotesSection, setShowNotesSection] = useState(false);
  const [selectedCycleForNotes, setSelectedCycleForNotes] = useState(null);
  
  const [createFormData, setCreateFormData] = useState({
    name: '',
    type: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    default_profit_percent: '10'
  });

  const [editDefaults, setEditDefaults] = useState({
    default_profit_percent: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cyclesRes, activeCycleRes, cashPoolRes] = await Promise.all([
        axiosInstance.get('/cycles'),
        axiosInstance.get('/cycles/active'),
        axiosInstance.get('/cash-pool/balance').catch(() => ({ data: null })),
      ]);
      setCycles(cyclesRes.data);
      setActiveCycle(activeCycleRes.data);
      // Cash pool can be returned as object or array; normalize to INR balance
      const cp = cashPoolRes?.data;
      if (Array.isArray(cp)) {
        const inr = cp.find((x) => x.currency === 'INR');
        setCashPoolINR(inr?.balance || 0);
      } else if (cp && typeof cp === 'object') {
        setCashPoolINR(cp.balance || cp.inr_balance || 0);
      }
      
      if (activeCycleRes.data) {
        const clientsRes = await axiosInstance.get(`/cycles/${activeCycleRes.data.id}/clients`);
        setCycleClients(clientsRes.data);
        setEditDefaults({
          default_profit_percent: activeCycleRes.data.default_profit_percent.toString()
        });
        // Don't pre-populate conversion rate - user must enter it at closing time
        
        // Initialize payout modes for INR clients (default to INR)
        const modes = {};
        clientsRes.data.forEach(c => {
          if (c.base_currency === 'INR') {
            modes[c.client_id] = 'INR';
          }
        });
        setClientPayoutModes(modes);
        
        // Fetch draft if exists (for draft_ready status)
        if (activeCycleRes.data.status === 'draft_ready') {
          try {
            const draftRes = await axiosInstance.get(`/cycles/${activeCycleRes.data.id}/draft`);
            setDraftData(draftRes.data);
            setCompanyProfit(draftRes.data.company_profit_usdt.toString());
            setConversionRate(draftRes.data.conversion_rate.toString());
            
            // Restore payout modes from draft
            const draftModes = {};
            draftRes.data.client_payouts.forEach(p => {
              if (p.base_currency === 'INR') {
                draftModes[p.client_id] = p.payout_currency;
              }
            });
            setClientPayoutModes(draftModes);
          } catch (e) {
            // No draft yet, that's ok
          }
        }
      }
    } catch (error) {
      toast.error('Failed to fetch cycles data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCycle = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post('/cycles', {
        ...createFormData,
        default_profit_percent: parseFloat(createFormData.default_profit_percent)
      });
      toast.success('Cycle created successfully');
      fetchData();
      setShowCreateForm(false);
      setCreateFormData({
        name: '',
        type: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        default_profit_percent: '10'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create cycle');
    }
  };

  const handleUpdateDefaults = async () => {
    try {
      await axiosInstance.put(`/cycles/${activeCycle.id}/defaults`, {
        default_profit_percent: parseFloat(editDefaults.default_profit_percent)
      });
      toast.success('Cycle defaults updated');
      setEditingDefaults(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update defaults');
    }
  };

  const handleUpdateCustomProfit = async (clientId, customPercent) => {
    if (!customPercent || isNaN(parseFloat(customPercent))) return;
    try {
      await axiosInstance.put(`/cycles/${activeCycle.id}/custom-profit`, {
        client_id: clientId,
        custom_profit_percent: parseFloat(customPercent)
      });
      toast.success('Custom profit % updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update custom profit %');
    }
  };

  const handlePreviewPayouts = async () => {
    if (companyProfit === '' || companyProfit === null || companyProfit === undefined) {
      toast.error('Please enter company profit amount (enter 0 for loss cycles)');
      return;
    }
    if (!conversionRate || conversionRate === '') {
      toast.error('Please enter the INR to USDT conversion rate');
      return;
    }
    const profitValue = parseFloat(companyProfit);
    if (isNaN(profitValue) || profitValue < 0) {
      toast.error('Company profit cannot be negative. Enter 0 for loss cycles.');
      return;
    }
    const rateValue = parseFloat(conversionRate);
    if (isNaN(rateValue) || rateValue <= 0) {
      toast.error('Please enter a valid conversion rate');
      return;
    }
    try {
      const response = await axiosInstance.post(`/cycles/${activeCycle.id}/preview-payouts`, {
        total_profit_made_usdt: profitValue,
        conversion_rate: rateValue
      });
      setPreviewData(response.data);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview payouts');
    }
  };

  const handleCloseCycleClick = () => {
    if (companyProfit === '' || companyProfit === null || companyProfit === undefined) {
      toast.error('Please enter company profit and preview before closing');
      return;
    }
    
    const profitValue = parseFloat(companyProfit);
    if (isNaN(profitValue) || profitValue < 0) {
      toast.error('Company profit cannot be negative. Enter 0 for loss cycles.');
      return;
    }
    
    setShowCloseConfirm(true);
  };

  const handleCloseCycle = async () => {
    setIsClosing(true);
    try {
      const profitValue = parseFloat(companyProfit);
      const response = await axiosInstance.post(`/cycles/${activeCycle.id}/close`, {
        total_profit_made_usdt: profitValue
      });
      // Calculate if it's a loss based on payouts vs profit
      const totalPayouts = response.data.reduce((sum, p) => sum + (p.profit_amount || 0), 0);
      const isLoss = totalPayouts > profitValue;
      toast.success(isLoss 
        ? `Cycle closed with company loss. Payouts generated.` 
        : 'Cycle closed successfully! Payouts generated.');
      setPayouts(response.data);
      setShowPreviewModal(false);
      setShowCloseConfirm(false);
      setShowPayoutsModal(true);
      setSelectedCycleForPayouts(activeCycle.id);
      setCompanyProfit('');
      setConversionRate(''); // Reset conversion rate
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close cycle');
    } finally {
      setIsClosing(false);
    }
  };

  const fetchPayouts = async (cycleId) => {
    try {
      const response = await axiosInstance.get(`/cycles/${cycleId}/payouts`);
      setPayouts(response.data);
      setSelectedCycleForPayouts(cycleId);
      setShowPayoutsModal(true);
    } catch (error) {
      toast.error('Failed to fetch payouts');
    }
  };

  const markPayoutPaid = async (payoutId) => {
    try {
      await axiosInstance.put(`/payouts/${payoutId}/mark-paid`);
      toast.success('Payout marked as paid');
      fetchPayouts(selectedCycleForPayouts);
    } catch (error) {
      toast.error('Failed to update payout');
    }
  };

  const reinvestPayout = async (payoutId, clientCode) => {
    if (!window.confirm(`Reinvest payout for ${clientCode}? This will add the profit to their capital.`)) {
      return;
    }
    try {
      await axiosInstance.put(`/payouts/${payoutId}/reinvest`);
      toast.success(`Payout reinvested for ${clientCode}`);
      fetchPayouts(selectedCycleForPayouts);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reinvest payout');
    }
  };

  const handleClearData = async () => {
    if (clearDataStep === 1) {
      setClearDataStep(2);
    } else if (clearDataStep === 2) {
      setClearDataStep(3);
    } else if (clearDataStep === 3) {
      if (clearDataConfirmText !== 'DELETE ALL DATA') {
        toast.error('Please type "DELETE ALL DATA" to confirm');
        return;
      }
      try {
        await axiosInstance.post('/admin/clear-data');
        toast.success('All data has been cleared');
        setShowClearDataModal(false);
        setClearDataStep(1);
        setClearDataConfirmText('');
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to clear data');
      }
    }
  };

  const closeClearDataModal = () => {
    setShowClearDataModal(false);
    setClearDataStep(1);
    setClearDataConfirmText('');
  };

  const handleReopenCycleClick = (cycleId, cycleName) => {
    setCycleToReopen({ id: cycleId, name: cycleName });
    setShowReopenConfirm(true);
  };

  const handleReopenCycle = async () => {
    if (!cycleToReopen) return;
    
    try {
      const response = await axiosInstance.put(`/cycles/${cycleToReopen.id}/reopen`);
      toast.success(`Cycle reopened! ${response.data.payouts_deleted} payouts deleted.`);
      setShowPayoutsModal(false);
      setShowReopenConfirm(false);
      setCycleToReopen(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen cycle');
    }
  };

  const handleCancelReopen = async (cycleId, cycleName) => {
    if (!window.confirm(`Cancel reopen and close cycle "${cycleName}" again?\n\n⚠️ WARNING: If payouts were deleted during reopen, they will NOT be restored.\n\nTo regenerate payouts, you should instead:\n1. Keep the cycle active\n2. Enter Company Profit\n3. Use "Save Draft" → "Finalize & Close"\n\nDo you still want to close without payouts?`)) {
      return;
    }
    
    try {
      const response = await axiosInstance.put(`/cycles/${cycleId}/cancel-reopen`);
      if (response.data.needs_proper_close) {
        toast.warning(`Cycle "${cycleName}" closed but has NO PAYOUTS. Consider reopening and closing properly to generate payouts.`, { duration: 8000 });
      } else {
        toast.success(`Cycle "${cycleName}" has been closed.`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel reopen');
    }
  };

  const handleRecalculateClosedCycles = async () => {
    if (!window.confirm('This will recalculate all closed cycles using their stored conversion rates.\n\nThis fixes historical data where USDT values were calculated with wrong rates.\n\nProceed?')) {
      return;
    }
    
    try {
      const response = await axiosInstance.post('/admin/recalculate-closed-cycles');
      const results = response.data.results || [];
      
      if (results.length === 0) {
        toast.info('No closed cycles found to recalculate');
      } else {
        toast.success(`Recalculated ${results.length} closed cycle(s). Refresh to see updated values.`);
        fetchData(); // Refresh the data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to recalculate cycles');
    }
  };

  const handleFixLedgerUsdt = async () => {
    if (!window.confirm('This will fix all ledger entries that have missing USDT equivalent values.\n\nThis recalculates AUM for clients whose deposits were not properly converted.\n\nProceed?')) {
      return;
    }
    
    try {
      const response = await axiosInstance.post('/admin/fix-ledger-usdt');
      toast.success(`Fixed ${response.data.entries_fixed} ledger entries using rate ${response.data.conversion_rate_used}. Refresh to see updated AUM values.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix ledger data');
    }
  };

  const handleFixExpensesUsdt = async () => {
    if (!window.confirm('This will recalculate USDT equivalent for ALL INR expenses using the latest cycle conversion rate.\n\nThis ensures all expense data uses a consistent rate.\n\nProceed?')) {
      return;
    }
    
    try {
      const response = await axiosInstance.post('/admin/fix-expenses-usdt');
      toast.success(`Fixed ${response.data.expenses_fixed} expenses using rate ${response.data.conversion_rate_used}.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix expense data');
    }
  };

  const handleSaveDraft = async () => {
    if (companyProfit === '' || companyProfit === null || companyProfit === undefined) {
      toast.error('Please enter company profit amount (enter 0 for loss cycles)');
      return;
    }
    
    const profitValue = parseFloat(companyProfit);
    if (isNaN(profitValue) || profitValue < 0) {
      toast.error('Company profit cannot be negative. Enter 0 for loss cycles.');
      return;
    }
    
    // Conversion rate is REQUIRED
    if (!conversionRate || conversionRate === '') {
      toast.error('Please enter the INR to USDT conversion rate');
      return;
    }
    
    const rateValue = parseFloat(conversionRate);
    if (isNaN(rateValue) || rateValue <= 0) {
      toast.error('Please enter a valid conversion rate');
      return;
    }
    
    try {
      // Build client_payouts array for INR clients
      const clientPayouts = cycleClients
        .filter(c => c.base_currency === 'INR')
        .map(c => ({
          client_id: c.client_id,
          payout_currency: clientPayoutModes[c.client_id] || 'INR'
        }));
      
      const response = await axiosInstance.post(`/cycles/${activeCycle.id}/save-draft`, {
        company_profit_usdt: profitValue,
        conversion_rate: rateValue,
        client_payouts: clientPayouts
      });
      
      toast.success(profitValue === 0 ? 'Draft saved (0 profit cycle)' : 'Draft saved successfully!');
      setDraftData(response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save draft');
    }
  };

  const handleDeleteDraft = async () => {
    if (!window.confirm('Delete draft and revert to active status? You can create a new draft later.')) {
      return;
    }
    
    try {
      await axiosInstance.delete(`/cycles/${activeCycle.id}/draft`);
      toast.success('Draft deleted');
      setDraftData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete draft');
    }
  };

  const handleDeleteCycle = async (cycleId, cycleName) => {
    const confirmText = `Delete in-progress cycle "${cycleName}"?\n\n` +
      `This will permanently remove the cycle, any saved draft, and any custom profit overrides. ` +
      `Closed cycles cannot be deleted.\n\nProceed?`;
    if (!window.confirm(confirmText)) return;

    try {
      await axiosInstance.delete(`/cycles/${cycleId}`);
      toast.success(`Cycle "${cycleName}" deleted`);
      setDraftData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete cycle');
    }
  };

  const downloadPrepSheet = async () => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/cycles/${activeCycle.id}/prep-sheet-pdf`;
      const token = localStorage.getItem('token');
      const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `prep_sheet_${activeCycle.name.replace(/\s+/g, '_')}.pdf`;
      link.click();
      toast.success('Prep sheet downloaded');
    } catch (error) {
      toast.error('Failed to download prep sheet');
    }
  };

  const handleFinalizeAndCloseClick = () => {
    if (!draftData) {
      toast.error('Please save draft first');
      return;
    }
    setShowCloseConfirm(true);
  };

  const handleFinalizeAndClose = async () => {
    setIsClosing(true);
    try {
      const response = await axiosInstance.post(`/cycles/${activeCycle.id}/close`, {
        total_profit_made_usdt: parseFloat(companyProfit)
      });
      toast.success('Cycle closed successfully! Payouts generated.');
      setPayouts(response.data);
      setShowPayoutsModal(true);
      setShowCloseConfirm(false);
      setSelectedCycleForPayouts(activeCycle.id);
      setDraftData(null);
      setCompanyProfit('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close cycle');
    } finally {
      setIsClosing(false);
    }
  };

  // Calculate live distribution summary
  const calculateDistributionSummary = () => {
    const rate = parseFloat(conversionRate) || 85;
    let totalINR = 0;
    let totalUSDT = 0;
    
    cycleClients.forEach(client => {
      if (client.base_currency === 'INR') {
        const payoutMode = clientPayoutModes[client.client_id] || 'INR';
        if (payoutMode === 'USDT') {
          totalUSDT += client.calculated_profit_base / rate;
        } else {
          totalINR += client.calculated_profit_base;
        }
      } else {
        totalUSDT += client.calculated_profit_usdt;
      }
    });
    
    return { totalINR, totalUSDT };
  };

  // Cycle Notes functions
  const fetchCycleNotes = async (cycleId) => {
    try {
      const response = await axiosInstance.get(`/cycles/${cycleId}/notes`);
      setCycleNotes(response.data);
    } catch (error) {
      toast.error('Failed to fetch notes');
    }
  };

  const handleAddNote = async (cycleId) => {
    if (!newNoteContent.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    try {
      await axiosInstance.post(`/cycles/${cycleId}/notes`, {
        content: newNoteContent
      });
      toast.success('Note added');
      setNewNoteContent('');
      fetchCycleNotes(cycleId);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (cycleId, noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      await axiosInstance.delete(`/cycles/${cycleId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchCycleNotes(cycleId);
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const openNotesForCycle = (cycleId) => {
    setSelectedCycleForNotes(cycleId);
    setShowNotesSection(true);
    fetchCycleNotes(cycleId);
  };

  // Calculate P&L preview
  const calculatePnLPreview = () => {
    if (companyProfit === '' || companyProfit === null || !draftData) return null;
    if (!conversionRate || conversionRate === '') return null; // Require conversion rate
    
    const profitMade = parseFloat(companyProfit) || 0;
    const rate = parseFloat(conversionRate);
    if (!rate || rate <= 0) return null;
    
    const distributed = draftData.summary.total_usdt_required + 
                       (draftData.summary.total_inr_cash_required / rate);
    const retention = profitMade - distributed;
    const totalAum = cycleClients.reduce((sum, c) => sum + (c.eligible_capital_usdt || 0), 0);
    const clientRate = totalAum > 0 ? (distributed / totalAum * 100) : 0;
    const companyRate = totalAum > 0 ? (retention / totalAum * 100) : 0;
    
    return {
      profitMade,
      distributed,
      retention,
      clientRate,
      companyRate,
      isLoss: retention < 0
    };
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="cycles-page">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Profit Cycles</p>
            <h1 className="cb-h1">Cycles</h1>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {user.is_admin && (
              <button
                onClick={() => setShowClearDataModal(true)}
                data-testid="clear-data-button"
                className="cb-btn-secondary cb-btn-secondary--negative"
              >
                <Trash2 className="w-4 h-4" />
                Clear Data
              </button>
            )}
            {user.is_admin && (
              <button
                onClick={handleFixLedgerUsdt}
                data-testid="fix-aum-button"
                className="cb-btn-secondary"
              >
                <RefreshCw className="w-4 h-4" />
                Fix AUM Data
              </button>
            )}
            {user.is_admin && (
              <button
                onClick={handleFixExpensesUsdt}
                data-testid="fix-expenses-button"
                className="cb-btn-secondary"
              >
                <RefreshCw className="w-4 h-4" />
                Fix Expenses
              </button>
            )}
            {user.is_admin && (
              <button
                onClick={handleRecalculateClosedCycles}
                data-testid="recalculate-button"
                className="cb-btn-secondary"
              >
                <RefreshCw className="w-4 h-4" />
                Fix Historical Data
              </button>
            )}
            <button
              onClick={() => setShowCreateForm(true)}
              data-testid="create-cycle-button"
              className="cb-btn-primary"
              disabled={activeCycle !== null}
            >
              <Plus className="w-4 h-4" />
              Create Cycle
            </button>
          </div>
        </div>
        <hr className="cb-divider" />
      </div>

      {activeCycle && (
        <div className="cb-notice items-start mb-6" style={{ cursor: 'default' }}>
          <div className="flex-1">
            <p className="cb-notice-sub">
              <strong style={{ color: 'var(--cb-ink)' }}>Note:</strong> Only one cycle can be active at a time. Close the current cycle before creating a new one.
            </p>
          </div>
        </div>
      )}

      {/* Active Cycle Section */}
      {activeCycle && (
        <div className="mb-8">
          <div className="cb-card" style={{ padding: 'var(--cb-space-6)' }} data-testid="active-cycle-section">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="cb-section-title" style={{ fontSize: '1.5rem' }}>
                  Active Cycle: {activeCycle.name}
                  <EditedBadge
                    lastEditedAt={activeCycle.last_edited_at}
                    lastEditedBy={activeCycle.last_edited_by}
                    lastEditReason={activeCycle.last_edit_reason}
                  />
                </h2>
                <p className="cb-section-sub cb-num">
                  {new Date(activeCycle.start_date).toLocaleDateString()} - {new Date(activeCycle.end_date).toLocaleDateString()}
                </p>
              </div>
              {user.is_admin && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowWizard(true)}
                    data-testid="open-closing-wizard-btn"
                    className="cb-btn-primary"
                    title="Guided step-by-step closing"
                  >
                    <Save className="w-4 h-4" />
                    Close Cycle (Wizard)
                  </button>
                  <button
                    onClick={() => handleDeleteCycle(activeCycle.id, activeCycle.name)}
                    data-testid="delete-active-cycle-btn"
                    className="cb-btn-secondary cb-btn-secondary--negative"
                    title="Delete this in-progress cycle and start fresh"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Cycle
                  </button>
                </div>
              )}
            </div>

            {/* Editable Cycle Defaults */}
            <div className="mb-6" style={{ background: 'var(--cb-bone)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-4)', border: '1px solid var(--cb-line)' }}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="cb-section-title" style={{ fontSize: '1rem' }}>Cycle Settings</h3>
                {!editingDefaults ? (
                  <button
                    onClick={() => setEditingDefaults(true)}
                    className="cb-btn-secondary"
                    style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                    data-testid="edit-defaults-button"
                  >
                    <Edit2 className="w-3 h-3" />
                    Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={handleUpdateDefaults}
                      className="cb-btn-secondary cb-btn-secondary--positive"
                      style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                      data-testid="save-defaults-button"
                    >
                      <Check className="w-3 h-3" />
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingDefaults(false);
                        setEditDefaults({
                          default_profit_percent: activeCycle.default_profit_percent.toString()
                        });
                      }}
                      className="cb-btn-secondary"
                      style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="cb-card-label">Type</p>
                  <p className="cb-card-value cb-card-value--sm capitalize">{activeCycle.type.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="cb-card-label">Period</p>
                  <p className="cb-card-value cb-card-value--sm cb-num" style={{ fontSize: '1rem' }}>{activeCycle.start_date} to {activeCycle.end_date}</p>
                </div>
                <div>
                  <p className="cb-card-label">Default Profit %</p>
                  {editingDefaults ? (
                    <input
                      type="number"
                      step="0.1"
                      className="form-input w-24"
                      value={editDefaults.default_profit_percent}
                      onChange={(e) => setEditDefaults({...editDefaults, default_profit_percent: e.target.value})}
                      data-testid="edit-default-profit"
                    />
                  ) : (
                    <p className="cb-card-value cb-card-value--sm cb-num cb-positive">{activeCycle.default_profit_percent}%</p>
                  )}
                </div>
              </div>
            </div>

            {/* Distribution Planning Section */}
            <div className="mb-6" style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-4)' }}>
              <div className="flex justify-between items-center mb-3">
                <h3 className="cb-section-title flex items-center" style={{ fontSize: '1rem', gap: 'var(--cb-space-2)' }}>
                  <FileText className="w-5 h-5" />
                  Distribution Planning
                  {activeCycle.status === 'draft_ready' && (
                    <span className="cb-pill cb-pill--positive">Draft Ready</span>
                  )}
                </h3>
                {draftData && (
                  <span className="cb-muted cb-num" style={{ fontSize: '0.75rem' }}>
                    Last saved: {new Date(draftData.generated_at).toLocaleString()}
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="form-label">Company Profit Made (USDT) *</label>
                  <input
                    type="number"
                    data-testid="company-profit-input"
                    step="0.01"
                    className="form-input"
                    value={companyProfit}
                    onChange={(e) => setCompanyProfit(e.target.value)}
                    placeholder="Enter total profit in USDT (0 for loss)"
                  />
                </div>
                <div>
                  <label className="form-label">Conversion Rate (INR to USDT) <span style={{ color: 'var(--cb-negative)' }}>*</span></label>
                  <input
                    type="number"
                    data-testid="conversion-rate-input"
                    step="0.01"
                    className="form-input"
                    value={conversionRate}
                    onChange={(e) => setConversionRate(e.target.value)}
                    placeholder="Required - Enter rate"
                    required
                  />
                  <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-1)' }}>Required for all calculations. No default value.</p>
                </div>
              </div>
              
              {/* Distribution Summary */}
              {draftData && (
                <div className="cb-card mb-4" style={{ padding: 'var(--cb-space-3)', boxShadow: 'none' }}>
                  <h4 className="cb-card-label" style={{ marginBottom: 'var(--cb-space-2)' }}>Distribution Summary</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="cb-muted" style={{ fontSize: '0.8125rem' }}>Cash Required (INR)</p>
                      <p className="cb-num" style={{ fontFamily: 'var(--cb-serif)', fontSize: '1.25rem', color: 'var(--cb-ink)' }}>₹{draftData.summary.total_inr_cash_required.toLocaleString()}</p>
                      <p className="cb-muted cb-num" style={{ fontSize: '0.75rem' }}>
                        Cash Pool: ₹{draftData.summary.cash_pool_balance.toLocaleString()}
                        {draftData.summary.cash_shortfall > 0 && (
                          <span className="cb-negative" style={{ marginLeft: '4px', fontWeight: 500 }}>⚠ Shortfall: ₹{draftData.summary.cash_shortfall.toLocaleString()}</span>
                        )}
                        {draftData.summary.cash_shortfall === 0 && (
                          <span className="cb-positive" style={{ marginLeft: '4px', fontWeight: 500 }}>✓ Sufficient</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="cb-muted" style={{ fontSize: '0.8125rem' }}>USDT Required</p>
                      <p className="cb-num" style={{ fontFamily: 'var(--cb-serif)', fontSize: '1.25rem', color: 'var(--cb-ink)' }}>{draftData.summary.total_usdt_required.toLocaleString()} USDT</p>
                      <p className="cb-muted" style={{ fontSize: '0.75rem' }}>Prepare from exchange</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* P&L Preview */}
              {companyProfit && draftData && (() => {
                const pnl = calculatePnLPreview();
                if (!pnl) return null;
                return (
                  <div className="cb-card mb-4" style={{ padding: 'var(--cb-space-3)', boxShadow: 'none' }} data-testid="pnl-preview">
                    <h4 className="cb-card-label" style={{ marginBottom: 'var(--cb-space-2)' }}>Cycle P&L Preview</h4>
                    <div className="grid grid-cols-4 gap-3" style={{ fontSize: '0.875rem' }}>
                      <div>
                        <p className="cb-muted">Profit Made</p>
                        <p className="cb-num cb-positive" style={{ fontWeight: 500 }}>{pnl.profitMade.toLocaleString()} USDT</p>
                      </div>
                      <div>
                        <p className="cb-muted">To Distribute</p>
                        <p className="cb-num" style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>-{pnl.distributed.toLocaleString(undefined, {maximumFractionDigits: 2})} USDT</p>
                      </div>
                      <div>
                        <p className="cb-muted">{pnl.isLoss ? 'Company Loss' : 'Company Keeps'}</p>
                        <p className={`cb-num ${pnl.isLoss ? 'cb-negative' : 'cb-positive'}`} style={{ fontWeight: 500 }}>
                          {pnl.retention.toLocaleString(undefined, {maximumFractionDigits: 2})} USDT
                        </p>
                      </div>
                      <div>
                        <p className="cb-muted">Rates</p>
                        <p className="cb-num" style={{ fontSize: '0.75rem', color: 'var(--cb-ink)' }}>
                          Client: {pnl.clientRate.toFixed(2)}% | Co: {pnl.companyRate.toFixed(2)}%
                        </p>
                        <p className={`cb-num ${pnl.isLoss ? 'cb-negative' : 'cb-positive'}`} style={{ fontSize: '0.75rem', fontWeight: 500 }}>
                          {pnl.isLoss ? `⚠ Company Loss: ${Math.abs(pnl.retention).toLocaleString(undefined, {maximumFractionDigits: 2})} USDT` : '✓ Surplus'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={handlePreviewPayouts}
                  data-testid="preview-payouts-button"
                  className="cb-btn-secondary"
                  disabled={companyProfit === '' || !conversionRate}
                >
                  <Eye className="w-4 h-4" />
                  Preview Payouts
                </button>
                <button
                  onClick={handleSaveDraft}
                  data-testid="save-draft-button"
                  className="cb-btn-primary"
                  disabled={companyProfit === '' || !conversionRate}
                >
                  <Save className="w-4 h-4" />
                  Save Draft
                </button>
                {draftData && (
                  <>
                    <button
                      onClick={downloadPrepSheet}
                      data-testid="download-prep-sheet-button"
                      className="cb-btn-secondary"
                    >
                      <FileDown className="w-4 h-4" />
                      Download Prep Sheet
                    </button>
                    <button
                      onClick={handleDeleteDraft}
                      data-testid="delete-draft-button"
                      className="cb-btn-secondary cb-btn-secondary--negative"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Draft
                    </button>
                    <button
                      onClick={handleFinalizeAndCloseClick}
                      data-testid="finalize-close-button"
                      className="cb-btn-primary"
                    >
                      <Check className="w-4 h-4" />
                      Finalize & Close
                    </button>
                  </>
                )}
              </div>
              {!draftData && companyProfit && (
                <p className="cb-muted" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-2)' }}>
                  Save draft to generate distribution prep sheet for your distribution agent.
                </p>
              )}
            </div>

            {/* INR Clients Table */}
            {cycleClients.filter(c => c.base_currency === 'INR').length > 0 && (
              <div className="mb-6">
                <h3 className="cb-section-title mb-3" style={{ fontSize: '1rem' }}>INR Clients</h3>
                <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
                  <table data-testid="cycle-inr-clients-table" className="cb-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>Client Code</th>
                        <th>Client Name</th>
                        <th className="cb-num">Eligible Capital (INR)</th>
                        <th className="cb-num">USDT Equivalent</th>
                        <th className="cb-num">Profit %</th>
                        <th className="cb-num">Custom %</th>
                        <th className="cb-num">Profit (INR)</th>
                        <th>Pay Mode</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cycleClients.filter(c => c.base_currency === 'INR').map((client) => (
                        <tr key={client.client_id}>
                          <td style={{ fontWeight: 500 }}>{client.client_code}</td>
                          <td>{client.client_name}</td>
                          <td className="cb-num">₹{(client.eligible_capital_base || 0).toLocaleString(undefined, {maximumFractionDigits: 0})}</td>
                          <td className="cb-num cb-muted">{client.eligible_capital_usdt.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                          <td className="cb-num">{client.custom_profit_percent || client.default_profit_percent}%</td>
                          <td className="cb-num">
                            <input
                              type="number"
                              data-testid={`custom-profit-input-${client.client_code}`}
                              step="0.1"
                              className="form-input w-20"
                              defaultValue={client.custom_profit_percent || ''}
                              onBlur={(e) => handleUpdateCustomProfit(client.client_id, e.target.value)}
                              placeholder="%"
                            />
                          </td>
                          <td className="cb-num" style={{ fontWeight: 500 }}>
                            ₹{client.calculated_profit_base.toLocaleString(undefined, {maximumFractionDigits: 0})}
                          </td>
                          <td>
                            <div className="flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name={`pay-mode-${client.client_id}`}
                                  data-testid={`pay-mode-inr-${client.client_code}`}
                                  checked={clientPayoutModes[client.client_id] !== 'USDT'}
                                  onChange={() => setClientPayoutModes(prev => ({...prev, [client.client_id]: 'INR'}))}
                                  className="mr-1"
                                />
                                <span style={{ fontSize: '0.8125rem' }}>INR</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name={`pay-mode-${client.client_id}`}
                                  data-testid={`pay-mode-usdt-${client.client_code}`}
                                  checked={clientPayoutModes[client.client_id] === 'USDT'}
                                  onChange={() => setClientPayoutModes(prev => ({...prev, [client.client_id]: 'USDT'}))}
                                  className="mr-1"
                                />
                                <span style={{ fontSize: '0.8125rem' }}>USDT</span>
                              </label>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {/* Total Row for INR Clients */}
                      <tr style={{ background: 'var(--cb-hover)', fontWeight: 600, borderTop: '1px solid var(--cb-line)' }}>
                        <td colSpan="2" style={{ textAlign: 'right' }}>TOTAL INR Clients</td>
                        <td className="cb-num">
                          ₹{cycleClients.filter(c => c.base_currency === 'INR').reduce((sum, c) => sum + (c.eligible_capital_base || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td className="cb-num cb-muted">
                          {cycleClients.filter(c => c.base_currency === 'INR').reduce((sum, c) => sum + (c.eligible_capital_usdt || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 2})} USDT
                        </td>
                        <td colSpan="2"></td>
                        <td className="cb-num">
                          ₹{cycleClients.filter(c => c.base_currency === 'INR').reduce((sum, c) => sum + (c.calculated_profit_base || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                        </td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* USDT Clients Table */}
            {cycleClients.filter(c => c.base_currency === 'USDT').length > 0 && (
              <div>
                <h3 className="cb-section-title mb-3" style={{ fontSize: '1rem' }}>USDT Clients</h3>
                <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
                  <table data-testid="cycle-usdt-clients-table" className="cb-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>Client Code</th>
                        <th>Client Name</th>
                        <th className="cb-num">Eligible Capital (USDT)</th>
                        <th className="cb-num">Profit %</th>
                        <th className="cb-num">Custom %</th>
                        <th className="cb-num">Profit (USDT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cycleClients.filter(c => c.base_currency === 'USDT').map((client) => (
                        <tr key={client.client_id}>
                          <td style={{ fontWeight: 500 }}>{client.client_code}</td>
                          <td>{client.client_name}</td>
                          <td className="cb-num">{client.eligible_capital_usdt.toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                          <td className="cb-num">{client.custom_profit_percent || client.default_profit_percent}%</td>
                          <td className="cb-num">
                            <input
                              type="number"
                              data-testid={`custom-profit-input-${client.client_code}`}
                              step="0.1"
                              className="form-input w-20"
                              defaultValue={client.custom_profit_percent || ''}
                              onBlur={(e) => handleUpdateCustomProfit(client.client_id, e.target.value)}
                              placeholder="%"
                            />
                          </td>
                          <td className="cb-num" style={{ fontWeight: 500 }}>
                            {client.calculated_profit_usdt.toLocaleString(undefined, {maximumFractionDigits: 2})}
                          </td>
                        </tr>
                      ))}
                      {/* Total Row for USDT Clients */}
                      <tr style={{ background: 'var(--cb-hover)', fontWeight: 600, borderTop: '1px solid var(--cb-line)' }}>
                        <td colSpan="2" style={{ textAlign: 'right' }}>TOTAL USDT Clients</td>
                        <td className="cb-num">
                          {cycleClients.filter(c => c.base_currency === 'USDT').reduce((sum, c) => sum + (c.eligible_capital_usdt || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 2})} USDT
                        </td>
                        <td colSpan="2"></td>
                        <td className="cb-num">
                          {cycleClients.filter(c => c.base_currency === 'USDT').reduce((sum, c) => sum + (c.calculated_profit_usdt || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 2})} USDT
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Grand Total - All Clients */}
            {cycleClients.length > 0 && (
              <div className="cb-card mt-4" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="cb-card-label">Total Clients</p>
                    <p className="cb-card-value cb-card-value--sm cb-num">{cycleClients.length}</p>
                  </div>
                  <div>
                    <p className="cb-card-label">Total Capital (USDT)</p>
                    <p className="cb-card-value cb-card-value--sm cb-num">
                      {cycleClients.reduce((sum, c) => sum + (c.eligible_capital_usdt || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 2})}
                    </p>
                  </div>
                  <div>
                    <p className="cb-card-label">INR Capital</p>
                    <p className="cb-card-value cb-card-value--sm cb-num">
                      ₹{cycleClients.filter(c => c.base_currency === 'INR').reduce((sum, c) => sum + (c.eligible_capital_base || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 0})}
                    </p>
                  </div>
                  <div>
                    <p className="cb-card-label">Total Profit (USDT)</p>
                    <p className="cb-card-value cb-card-value--sm cb-num">
                      {draftData && conversionRate ? (
                        (draftData.summary.total_usdt_required + (draftData.summary.total_inr_cash_required / parseFloat(conversionRate))).toLocaleString(undefined, {maximumFractionDigits: 2})
                      ) : (
                        cycleClients.reduce((sum, c) => sum + (c.calculated_profit_usdt || 0), 0).toLocaleString(undefined, {maximumFractionDigits: 2})
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {cycleClients.length === 0 && (
              <div className="text-center cb-muted" style={{ padding: 'var(--cb-space-7) 0' }}>
                No active clients found. Add clients to see them in this cycle.
              </div>
            )}
          </div>
        </div>
      )}

      {/* All Cycles List */}
      <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
        <h2 className="cb-section-title" style={{ padding: 'var(--cb-space-4) var(--cb-space-5)', borderBottom: '1px solid var(--cb-line)', fontSize: '1rem' }}>All Cycles</h2>
        <table data-testid="cycles-table" className="cb-table" style={{ minWidth: '100%' }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Period</th>
              <th>Status</th>
              <th className="cb-num">Default %</th>
              <th className="cb-num">P&L (USDT)</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {cycles.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center cb-muted">No cycles found</td>
              </tr>
            ) : (
              cycles.map((cycle) => (
                <tr key={cycle.id}>
                  <td style={{ fontWeight: 500 }}>
                    {cycle.name}
                    <EditedBadge
                      lastEditedAt={cycle.last_edited_at}
                      lastEditedBy={cycle.last_edited_by}
                      lastEditReason={cycle.last_edit_reason}
                    />
                  </td>
                  <td className="capitalize">{cycle.type.replace('_', ' ')}</td>
                  <td className="cb-muted cb-num" style={{ fontSize: '0.8125rem' }}>
                    {new Date(cycle.start_date).toLocaleDateString()} - {new Date(cycle.end_date).toLocaleDateString()}
                  </td>
                  <td>
                    <span className={`cb-pill ${cycle.status === 'active' || cycle.status === 'draft_ready' ? 'cb-pill--positive' : ''}`}>
                      {cycle.status === 'draft_ready' ? 'Draft Ready' : cycle.status}
                    </span>
                  </td>
                  <td className="cb-num">{cycle.default_profit_percent}%</td>
                  <td className="cb-num">
                    {cycle.status === 'closed' && cycle.company_retention_usdt !== undefined ? (
                      <span className={cycle.company_retention_usdt >= 0 ? 'cb-positive' : 'cb-negative'} style={{ fontWeight: 500 }}>
                        {cycle.company_retention_usdt >= 0 ? '✓' : '⚠'} {cycle.company_retention_usdt?.toLocaleString() || 0}
                      </span>
                    ) : <span className="cb-muted">-</span>}
                  </td>
                  <td style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--cb-space-1)' }}>
                    <button
                      onClick={() => openNotesForCycle(cycle.id)}
                      data-testid={`notes-${cycle.id}`}
                      className="cb-btn-secondary"
                      style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                    >
                      Notes
                    </button>
                    {cycle.status === 'closed' && (
                      <>
                        <button
                          onClick={() => fetchPayouts(cycle.id)}
                          data-testid={`view-payouts-${cycle.id}`}
                          className="cb-btn-secondary"
                          style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                        >
                          Payouts
                        </button>
                        {user.is_admin && (
                          <button
                            onClick={() => handleReopenCycleClick(cycle.id, cycle.name)}
                            data-testid={`reopen-cycle-${cycle.id}`}
                            className="cb-btn-secondary cb-btn-secondary--negative"
                            style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                          >
                            Reopen
                          </button>
                        )}
                      </>
                    )}
                    {cycle.status === 'active' && user.is_admin && new Date(cycle.end_date) < new Date() && (
                      <button
                        onClick={() => handleCancelReopen(cycle.id, cycle.name)}
                        data-testid={`cancel-reopen-${cycle.id}`}
                        className="cb-btn-secondary cb-btn-secondary--negative"
                        style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                        title="Cancel reopen and close this cycle again"
                      >
                        Cancel Reopen
                      </button>
                    )}
                    {cycle.status !== 'closed' && user.is_admin && (
                      <button
                        onClick={() => handleDeleteCycle(cycle.id, cycle.name)}
                        data-testid={`delete-cycle-${cycle.id}`}
                        className="cb-btn-secondary cb-btn-secondary--negative"
                        style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                        title="Delete this in-progress cycle"
                      >
                        Delete
                      </button>
                    )}
                    {user.is_admin && (
                      <button
                        onClick={() => setEditCycle(cycle)}
                        data-testid={`edit-cycle-${cycle.id}`}
                        className="cb-btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                        title="Admin: edit cycle fields"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Cycle Notes Modal */}
      {showNotesSection && selectedCycleForNotes && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-2xl max-h-[80vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Cycle Notes</h2>
              <button onClick={() => setShowNotesSection(false)} data-testid="close-notes-modal" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Add Note */}
            <div className="mb-4">
              <textarea
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                data-testid="new-note-input"
                className="form-input w-full h-24"
                placeholder="Add a note about this cycle..."
              />
              <button
                onClick={() => handleAddNote(selectedCycleForNotes)}
                data-testid="add-note-button"
                className="cb-btn-primary mt-2"
              >
                Add Note
              </button>
            </div>
            
            {/* Notes List */}
            <div className="space-y-3">
              {cycleNotes.length === 0 ? (
                <p className="cb-muted text-center" style={{ padding: 'var(--cb-space-4) 0' }}>No notes yet. Add one above.</p>
              ) : (
                cycleNotes.map((note) => (
                  <div key={note.id} style={{ background: 'var(--cb-bone)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-3)', border: '1px solid var(--cb-line)' }}>
                    <div className="flex justify-between items-start mb-2">
                      <span className="cb-muted" style={{ fontSize: '0.75rem' }}>
                        {note.created_by_name} • {new Date(note.created_at).toLocaleString()}
                        {note.updated_at && ' (edited)'}
                      </span>
                      {user.role === 'admin' && (
                        <button
                          onClick={() => handleDeleteNote(selectedCycleForNotes, note.id)}
                          data-testid={`delete-note-${note.id}`}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-negative)' }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <p style={{ color: 'var(--cb-ink)', whiteSpace: 'pre-wrap' }}>{note.content}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Cycle Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Create New Cycle</h2>
              <button onClick={() => setShowCreateForm(false)} data-testid="close-create-cycle-form" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateCycle} className="space-y-4">
              <div>
                <label className="form-label">Cycle Name *</label>
                <input
                  type="text"
                  data-testid="cycle-name-input"
                  className="form-input"
                  value={createFormData.name}
                  onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                  required
                  placeholder="e.g., January 2025"
                />
              </div>

              <div>
                <label className="form-label">Cycle Type *</label>
                <select
                  data-testid="cycle-type-select"
                  className="form-input"
                  value={createFormData.type}
                  onChange={(e) => setCreateFormData({ ...createFormData, type: e.target.value })}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="half_yearly">Half Yearly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Start Date *</label>
                  <input
                    type="date"
                    data-testid="cycle-start-date-input"
                    className="form-input"
                    value={createFormData.start_date}
                    onChange={(e) => setCreateFormData({ ...createFormData, start_date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">End Date *</label>
                  <input
                    type="date"
                    data-testid="cycle-end-date-input"
                    className="form-input"
                    value={createFormData.end_date}
                    onChange={(e) => setCreateFormData({ ...createFormData, end_date: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Default Profit % *</label>
                <input
                  type="number"
                  data-testid="cycle-default-profit-input"
                  step="0.1"
                  className="form-input"
                  value={createFormData.default_profit_percent}
                  onChange={(e) => setCreateFormData({ ...createFormData, default_profit_percent: e.target.value })}
                  required
                />
              </div>

              <div style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-line)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-3)' }}>
                <p style={{ fontSize: '0.875rem', color: 'var(--cb-ink)' }}>
                  <strong>Note:</strong> Conversion rate will be entered at cycle closing time, not at creation.
                </p>
              </div>

              <div className="flex gap-3">
                <button type="submit" data-testid="submit-create-cycle" className="cb-btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
                  Create Cycle
                </button>
                <button type="button" onClick={() => setShowCreateForm(false)} className="cb-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Payouts Modal */}
      {showPreviewModal && previewData && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-5xl max-h-[90vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Payout Preview</h2>
              <button onClick={() => setShowPreviewModal(false)} data-testid="close-preview-modal" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
                <p className="cb-card-label">Company Profit Input</p>
                <p className="cb-card-value cb-card-value--sm cb-num">{previewData.total_profit_input_usdt.toLocaleString()} USDT</p>
              </div>
              <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
                <p className="cb-card-label">Total INR Payout</p>
                <p className="cb-card-value cb-card-value--sm cb-num">₹{previewData.total_inr_payout.toLocaleString()}</p>
              </div>
              <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
                <p className="cb-card-label">Total USDT Payout</p>
                <p className="cb-card-value cb-card-value--sm cb-num">{previewData.total_usdt_payout.toLocaleString()} USDT</p>
              </div>
              <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
                <p className="cb-card-label">Company Retention</p>
                <p className={`cb-card-value cb-card-value--sm cb-num ${previewData.company_retention >= 0 ? 'cb-positive' : 'cb-negative'}`}>{previewData.company_retention.toLocaleString()} USDT</p>
              </div>
            </div>

            {/* INR Clients Preview */}
            {previewData.payouts.filter(p => p.base_currency === 'INR').length > 0 && (
              <div className="mb-6">
                <h3 className="cb-section-title mb-3" style={{ fontSize: '1rem' }}>INR Clients Preview</h3>
                <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="cb-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>Client Code</th>
                        <th>Client Name</th>
                        <th className="cb-num">Eligible Capital (INR)</th>
                        <th className="cb-num">USDT Equivalent</th>
                        <th className="cb-num">Profit %</th>
                        <th className="cb-num">Payout (INR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.payouts.filter(p => p.base_currency === 'INR').map((payout, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 500 }}>{payout.client_code}</td>
                          <td>{payout.client_name}</td>
                          <td className="cb-num">₹{(payout.eligible_capital_base || 0).toLocaleString()}</td>
                          <td className="cb-num cb-muted">{payout.eligible_capital_usdt.toLocaleString()}</td>
                          <td className="cb-num">{payout.profit_percent}%</td>
                          <td className="cb-num" style={{ fontWeight: 500 }}>₹{payout.profit_amount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* USDT Clients Preview */}
            {previewData.payouts.filter(p => p.base_currency === 'USDT').length > 0 && (
              <div className="mb-6">
                <h3 className="cb-section-title mb-3" style={{ fontSize: '1rem' }}>USDT Clients Preview</h3>
                <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="cb-table" style={{ minWidth: '100%' }}>
                    <thead>
                      <tr>
                        <th>Client Code</th>
                        <th>Client Name</th>
                        <th className="cb-num">Eligible Capital (USDT)</th>
                        <th className="cb-num">Profit %</th>
                        <th className="cb-num">Payout (USDT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.payouts.filter(p => p.base_currency === 'USDT').map((payout, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 500 }}>{payout.client_code}</td>
                          <td>{payout.client_name}</td>
                          <td className="cb-num">{payout.eligible_capital_usdt.toLocaleString()}</td>
                          <td className="cb-num">{payout.profit_percent}%</td>
                          <td className="cb-num" style={{ fontWeight: 500 }}>{payout.profit_amount.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button 
                onClick={() => setShowPreviewModal(false)} 
                className="cb-btn-secondary"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payouts Modal */}
      {showPayoutsModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-5xl max-h-[90vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Cycle Payouts</h2>
              <div className="flex items-center" style={{ gap: 'var(--cb-space-3)' }}>
                <button 
                  onClick={() => {
                    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cycles/${selectedCycleForPayouts}/payouts/pdf`;
                    const token = localStorage.getItem('token');
                    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
                      .then(res => res.blob())
                      .then(blob => {
                        const link = document.createElement('a');
                        link.href = URL.createObjectURL(blob);
                        link.download = `payouts_${selectedCycleForPayouts}.pdf`;
                        link.click();
                      })
                      .catch(() => toast.error('Failed to download PDF'));
                  }}
                  data-testid="download-pdf-button"
                  className="cb-btn-secondary"
                >
                  <FileDown className="w-4 h-4" />
                  Download PDF
                </button>
                <button onClick={() => setShowPayoutsModal(false)} data-testid="close-payouts-modal" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {payouts.length === 0 ? (
              <div className="text-center cb-muted" style={{ padding: 'var(--cb-space-7) 0' }}>
                No payouts found for this cycle.
              </div>
            ) : (
              <>
                {/* Totals Summary */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
                    <p className="cb-card-label">Cash/Bank Payouts (INR)</p>
                    <p className="cb-card-value cb-card-value--sm cb-num">
                      ₹{payouts.filter(p => p.payout_currency === 'INR' || (p.base_currency === 'INR' && !p.payout_currency)).reduce((sum, p) => sum + (p.payout_amount || p.profit_amount || 0), 0).toLocaleString()}
                    </p>
                    <div className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-2)' }}>
                      <span>Pending: ₹{payouts.filter(p => (p.payout_currency === 'INR' || (p.base_currency === 'INR' && !p.payout_currency)) && p.status === 'pending').reduce((sum, p) => sum + (p.payout_amount || p.profit_amount || 0), 0).toLocaleString()}</span>
                      <span style={{ margin: '0 var(--cb-space-2)' }}>|</span>
                      <span>Paid: ₹{payouts.filter(p => (p.payout_currency === 'INR' || (p.base_currency === 'INR' && !p.payout_currency)) && p.status === 'paid').reduce((sum, p) => sum + (p.payout_amount || p.profit_amount || 0), 0).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
                    <p className="cb-card-label">USDT Transfers</p>
                    <p className="cb-card-value cb-card-value--sm cb-num">
                      {payouts.filter(p => p.payout_currency === 'USDT' || p.base_currency === 'USDT').reduce((sum, p) => sum + (p.profit_amount || 0), 0).toLocaleString()} USDT
                    </p>
                    <div className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-2)' }}>
                      <span>Pending: {payouts.filter(p => (p.payout_currency === 'USDT' || p.base_currency === 'USDT') && p.status === 'pending').reduce((sum, p) => sum + (p.profit_amount || 0), 0).toLocaleString()}</span>
                      <span style={{ margin: '0 var(--cb-space-2)' }}>|</span>
                      <span>Paid: {payouts.filter(p => (p.payout_currency === 'USDT' || p.base_currency === 'USDT') && p.status === 'paid').reduce((sum, p) => sum + (p.profit_amount || 0), 0).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
                    <p className="cb-card-label">Reinvested</p>
                    <p className="cb-card-value cb-card-value--sm cb-num">
                      {payouts.filter(p => p.status === 'reinvested' || p.reinvested).length} clients
                    </p>
                    <div className="cb-muted cb-num" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-2)' }}>
                      <span>INR: ₹{payouts.filter(p => p.base_currency === 'INR' && (p.status === 'reinvested' || p.reinvested)).reduce((sum, p) => sum + (p.profit_amount || 0), 0).toLocaleString()}</span>
                      <span style={{ margin: '0 var(--cb-space-2)' }}>|</span>
                      <span>USDT: {payouts.filter(p => p.base_currency === 'USDT' && (p.status === 'reinvested' || p.reinvested)).reduce((sum, p) => sum + (p.profit_amount || 0), 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Cash/Bank Payouts (INR) */}
                {payouts.filter(p => (p.payout_currency === 'INR' || (!p.payout_currency && p.base_currency === 'INR')) && p.status !== 'reinvested').length > 0 && (
                  <div className="mb-6">
                    <h3 className="cb-section-title mb-3" style={{ fontSize: '1rem' }}>Cash/Bank Payouts (INR)</h3>
                    <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
                      <table data-testid="payouts-inr-table" className="cb-table" style={{ minWidth: '100%' }}>
                        <thead>
                          <tr>
                            <th>Client Code</th>
                            <th>Client Name</th>
                            <th className="cb-num">Eligible Capital</th>
                            <th className="cb-num">Profit %</th>
                            <th className="cb-num">Payout Amount</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.filter(p => (p.payout_currency === 'INR' || (!p.payout_currency && p.base_currency === 'INR')) && p.status !== 'reinvested').map((payout) => (
                            <tr key={payout.id}>
                              <td style={{ fontWeight: 500 }}>
                                {payout.client_code}
                                <EditedBadge
                                  lastEditedAt={payout.last_edited_at}
                                  lastEditedBy={payout.last_edited_by}
                                  lastEditReason={payout.last_edit_reason}
                                />
                              </td>
                              <td>{payout.client_name}</td>
                              <td className="cb-num">₹{(payout.eligible_capital_base || 0).toLocaleString()}</td>
                              <td className="cb-num">{payout.profit_percent}%</td>
                              <td className="cb-num" style={{ fontWeight: 500 }}>₹{(payout.payout_amount || payout.profit_amount || 0).toLocaleString()}</td>
                              <td>
                                <span className={`cb-pill ${payout.status === 'paid' ? 'cb-pill--positive' : ''}`}>
                                  {payout.status}
                                </span>
                              </td>
                              <td>
                                <div className="flex flex-wrap" style={{ gap: 'var(--cb-space-2)' }}>
                                  {payout.status === 'pending' && (
                                    <>
                                      <button
                                        onClick={() => setPayoutToPay(payout)}
                                        data-testid={`mark-paid-${payout.client_code}`}
                                        className="cb-btn-secondary cb-btn-secondary--positive"
                                        style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                      >
                                        <Check className="w-3 h-3" />
                                        Pay
                                      </button>
                                      <button
                                        onClick={() => reinvestPayout(payout.id, payout.client_code)}
                                        data-testid={`reinvest-${payout.client_code}`}
                                        className="cb-btn-secondary"
                                        style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                      >
                                        <RefreshCw className="w-3 h-3" />
                                        Reinvest
                                      </button>
                                    </>
                                  )}
                                  {user.is_admin && (
                                    <button
                                      onClick={() => setEditPayout(payout)}
                                      data-testid={`edit-payout-${payout.client_code}`}
                                      className="cb-btn-secondary"
                                      style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                      title="Admin: edit payout"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                      Edit
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* USDT Transfers */}
                {payouts.filter(p => p.payout_currency === 'USDT' || p.base_currency === 'USDT').filter(p => p.status !== 'reinvested').length > 0 && (
                  <div className="mb-6">
                    <h3 className="cb-section-title mb-3" style={{ fontSize: '1rem' }}>USDT Transfers</h3>
                    <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
                      <table data-testid="payouts-usdt-table" className="cb-table" style={{ minWidth: '100%' }}>
                        <thead>
                          <tr>
                            <th>Client Code</th>
                            <th>Client Name</th>
                            <th>Base Currency</th>
                            <th className="cb-num">Profit %</th>
                            <th className="cb-num">Payout (USDT)</th>
                            <th>Note</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.filter(p => (p.payout_currency === 'USDT' || p.base_currency === 'USDT') && p.status !== 'reinvested').map((payout) => (
                            <tr key={payout.id}>
                              <td style={{ fontWeight: 500 }}>
                                {payout.client_code}
                                <EditedBadge
                                  lastEditedAt={payout.last_edited_at}
                                  lastEditedBy={payout.last_edited_by}
                                  lastEditReason={payout.last_edit_reason}
                                />
                              </td>
                              <td>{payout.client_name}</td>
                              <td>{payout.base_currency}</td>
                              <td className="cb-num">{payout.profit_percent}%</td>
                              <td className="cb-num" style={{ fontWeight: 500 }}>
                                {(payout.profit_amount || 0).toLocaleString()} USDT
                              </td>
                              <td className="cb-muted" style={{ fontSize: '0.75rem' }}>
                                {payout.base_currency === 'INR' && payout.conversion_rate && `Converted @${payout.conversion_rate}`}
                                {payout.base_currency === 'USDT' && payout.payout_currency === 'INR' && payout.conversion_rate && (
                                  <span data-testid={`inr-paid-note-${payout.client_code}`}>
                                    Paid ₹{(payout.payout_amount || 0).toLocaleString()} @ {payout.conversion_rate}
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className={`cb-pill ${payout.status === 'paid' ? 'cb-pill--positive' : ''}`}>
                                  {payout.status}
                                </span>
                              </td>
                              <td>
                                <div className="flex flex-wrap" style={{ gap: 'var(--cb-space-2)' }}>
                                  {payout.status === 'pending' && (
                                    <>
                                      <button
                                        onClick={() => setPayoutToPay(payout)}
                                        data-testid={`mark-paid-${payout.client_code}`}
                                        className="cb-btn-secondary cb-btn-secondary--positive"
                                        style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                      >
                                        <Check className="w-3 h-3" />
                                        Pay
                                      </button>
                                      <button
                                        onClick={() => reinvestPayout(payout.id, payout.client_code)}
                                        data-testid={`reinvest-${payout.client_code}`}
                                        className="cb-btn-secondary"
                                        style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                      >
                                        <RefreshCw className="w-3 h-3" />
                                        Reinvest
                                      </button>
                                    </>
                                  )}
                                  {user.is_admin && (
                                    <button
                                      onClick={() => setEditPayout(payout)}
                                      data-testid={`edit-payout-usdt-${payout.client_code}`}
                                      className="cb-btn-secondary"
                                      style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                      title="Admin: edit payout"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                      Edit
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Reinvested */}
                {payouts.filter(p => p.status === 'reinvested' || p.reinvested).length > 0 && (
                  <div className="mb-6">
                    <h3 className="cb-section-title mb-3" style={{ fontSize: '1rem' }}>Reinvested (No Payout)</h3>
                    <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
                      <table data-testid="payouts-reinvested-table" className="cb-table" style={{ minWidth: '100%' }}>
                        <thead>
                          <tr>
                            <th>Client Code</th>
                            <th>Client Name</th>
                            <th>Base Currency</th>
                            <th className="cb-num">Profit Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payouts.filter(p => p.status === 'reinvested' || p.reinvested).map((payout) => (
                            <tr key={payout.id}>
                              <td style={{ fontWeight: 500 }}>{payout.client_code}</td>
                              <td>{payout.client_name}</td>
                              <td>{payout.base_currency}</td>
                              <td className="cb-num" style={{ fontWeight: 500 }}>
                                {payout.base_currency === 'INR' ? `₹${(payout.profit_amount || 0).toLocaleString()}` : `${(payout.profit_amount || 0).toLocaleString()} USDT`}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Clear Data Modal - Multi-step confirmation */}
      {showClearDataModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(11,11,10,0.55)' }}>
          <div className="cb-card w-full max-w-md" style={{ padding: 'var(--cb-space-6)' }}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title flex items-center" style={{ color: 'var(--cb-negative)', gap: 'var(--cb-space-2)' }}>
                <AlertTriangle className="w-6 h-6" />
                Clear All Data
              </h2>
              <button onClick={closeClearDataModal} data-testid="close-clear-data-modal" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {clearDataStep === 1 && (
              <div>
                <div className="mb-4" style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-negative)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-4)' }}>
                  <p style={{ color: 'var(--cb-negative)', fontWeight: 500, marginBottom: 'var(--cb-space-2)' }}>⚠ Warning: This action is irreversible.</p>
                  <p style={{ color: 'var(--cb-ink)', fontSize: '0.875rem' }}>
                    This will permanently delete ALL data from the system including:
                  </p>
                  <ul style={{ color: 'var(--cb-ink)', fontSize: '0.875rem', marginTop: 'var(--cb-space-2)', listStyle: 'disc', paddingLeft: 'var(--cb-space-5)' }}>
                    <li>All clients</li>
                    <li>All ledger entries</li>
                    <li>All cycles and payouts</li>
                    <li>All expenses</li>
                    <li>All cash pool data</li>
                  </ul>
                </div>
                <p className="cb-muted mb-4">Are you sure you want to proceed?</p>
                <div className="flex gap-3">
                  <button 
                    onClick={handleClearData}
                    data-testid="clear-data-step1-confirm"
                    className="cb-btn-primary"
                    style={{ flex: 1, justifyContent: 'center', background: 'var(--cb-negative)', borderColor: 'var(--cb-negative)' }}
                  >
                    Yes, Continue
                  </button>
                  <button onClick={closeClearDataModal} className="cb-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {clearDataStep === 2 && (
              <div>
                <div className="mb-4" style={{ background: 'var(--cb-bone)', border: '1px solid var(--cb-negative)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-4)' }}>
                  <p style={{ color: 'var(--cb-negative)', fontWeight: 500 }}>Second Confirmation Required</p>
                  <p style={{ color: 'var(--cb-ink)', fontSize: '0.875rem', marginTop: 'var(--cb-space-2)' }}>
                    You are about to delete ALL data. This cannot be recovered. User accounts will be preserved.
                  </p>
                </div>
                <p className="cb-muted mb-4">Do you really want to delete all data?</p>
                <div className="flex gap-3">
                  <button 
                    onClick={handleClearData}
                    data-testid="clear-data-step2-confirm"
                    className="cb-btn-primary"
                    style={{ flex: 1, justifyContent: 'center', background: 'var(--cb-negative)', borderColor: 'var(--cb-negative)' }}
                  >
                    Yes, I'm Sure
                  </button>
                  <button onClick={closeClearDataModal} className="cb-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {clearDataStep === 3 && (
              <div>
                <div className="mb-4" style={{ background: 'var(--cb-bone)', border: '2px solid var(--cb-negative)', borderRadius: 'var(--cb-radius)', padding: 'var(--cb-space-4)' }}>
                  <p style={{ color: 'var(--cb-negative)', fontWeight: 700 }}>FINAL CONFIRMATION</p>
                  <p style={{ color: 'var(--cb-ink)', fontSize: '0.875rem', marginTop: 'var(--cb-space-2)' }}>
                    Type <strong>"DELETE ALL DATA"</strong> to confirm deletion.
                  </p>
                </div>
                <input
                  type="text"
                  data-testid="clear-data-confirm-input"
                  className="form-input mb-4"
                  placeholder='Type "DELETE ALL DATA"'
                  value={clearDataConfirmText}
                  onChange={(e) => setClearDataConfirmText(e.target.value)}
                />
                <div className="flex gap-3">
                  <button 
                    onClick={handleClearData}
                    data-testid="clear-data-final-confirm"
                    className="cb-btn-primary"
                    style={{ flex: 1, justifyContent: 'center', background: 'var(--cb-negative)', borderColor: 'var(--cb-negative)' }}
                    disabled={clearDataConfirmText !== 'DELETE ALL DATA'}
                  >
                    Delete Everything
                  </button>
                  <button onClick={closeClearDataModal} className="cb-btn-secondary" style={{ flex: 1, justifyContent: 'center' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Close Cycle Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onConfirm={handleFinalizeAndClose}
        title="Close Cycle"
        message={
          parseFloat(companyProfit) === 0 
            ? `Close cycle "${activeCycle?.name}" with ZERO profit (loss cycle)? All payouts will still be generated based on client profit percentages.`
            : `Are you sure you want to close cycle "${activeCycle?.name}"?\n\nThis will:\n• Create payouts for all eligible clients\n• Lock the cycle permanently\n• This action cannot be undone`
        }
        confirmText="Yes, Close Cycle"
        cancelText="Cancel"
        variant="warning"
        loading={isClosing}
      />

      {/* Reopen Cycle Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showReopenConfirm}
        onClose={() => {
          setShowReopenConfirm(false);
          setCycleToReopen(null);
        }}
        onConfirm={handleReopenCycle}
        title="Reopen Cycle"
        message={`Are you sure you want to reopen cycle "${cycleToReopen?.name}"?\n\nThis will:\n• Delete all payouts from this cycle\n• Delete any reinvestment entries\n• Allow you to make corrections\n\nThis action cannot be undone.`}
        confirmText="Yes, Reopen"
        cancelText="Cancel"
        variant="danger"
      />

      {/* Admin Edit Modals */}
      {editCycle && (
        <EditCycleModal
          cycle={editCycle}
          onClose={() => setEditCycle(null)}
          onSaved={() => fetchData()}
        />
      )}
      {editPayout && (
        <EditPayoutModal
          payout={editPayout}
          onClose={() => setEditPayout(null)}
          onSaved={() => selectedCycleForPayouts && fetchPayouts(selectedCycleForPayouts)}
        />
      )}

      {/* Guided Closing Wizard */}
      {showWizard && activeCycle && (
        <ClosingWizard
          cycle={activeCycle}
          clients={cycleClients}
          cashPoolINR={cashPoolINR}
          onClose={() => setShowWizard(false)}
          onClosed={() => fetchData()}
        />
      )}

      {/* Pay Payout Modal */}
      {payoutToPay && (
        <PayPayoutModal
          payout={payoutToPay}
          onClose={() => setPayoutToPay(null)}
          onPaid={() => selectedCycleForPayouts && fetchPayouts(selectedCycleForPayouts)}
        />
      )}
    </div>
  );
};

export default Cycles;
